// Business rule constants - explicitly defined for clarity and testability
export const DEFAULT_LOCALE = "en";
export const JOB_POSTING_RECENTLY_THRESHOLD_DAYS = 5;

export function formatYear(
  date: string | null,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!date) return "";

  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return "";

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

export function formatYearRange(
  startDate: string | null,
  endDate: string | null,
): string {
  const start = formatYear(startDate);
  const end = formatYear(endDate);

  if (!start && !end) return "";
  if (!start) return end;
  if (!end) return start;
  return `${start} – ${end}`;
}

export function formatDateRange(
  startDate: string | null,
  endDate: string | null,
): string {
  const start = formatYear(startDate);
  const end = formatYear(endDate);

  if (start && !end) return `${start} – Present`;
  return formatYearRange(startDate, endDate);
}

/**
 * Formats a date string into a readable format using Next.js i18n
 * @param dateString - The date string to format (can be null or undefined)
 * @param locale - The locale to use for formatting
 * @returns Formatted date string in localized format or "N/A" if invalid
 *
 * @example
 * formatDate("2024-01-15", "en") // "1/15/24"
 * formatDate("2024-01-15", "en-US") // "1/15/24"
 * formatDate("2024-01-15", "en-GB") // "15/01/24"
 * formatDate(null, "en") // "N/A"
 * formatDate(undefined, "en") // "N/A"
 */
export function formatDate(
  dateString: string | null | undefined,
  locale: string,
): string {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    // Use Intl.DateTimeFormat for consistent i18n support
    return new Intl.DateTimeFormat(locale, {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
    }).format(date);
  } catch {
    return "N/A";
  }
}

/**
 * Formats a date string into a long readable format with time (e.g., "Jan 15, 2024. 10:35 AM")
 * Displays in the user's local timezone (detected from browser).
 * @param dateString - The date string to format (can be null or undefined)
 * @param locale - The locale to use for formatting (defaults to "en-US")
 * @returns Formatted date string in long format with time or empty string if invalid
 *
 * @example
 * formatDateLong("2024-01-15T10:35:00Z") // "Jan 15, 2024. 5:35 AM" (in EST)
 * formatDateLong(null) // ""
 * formatDateLong(undefined) // ""
 */
export function formatDateLong(
  dateString: string | null | undefined,
  locale: string = "en-US",
): string {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const datePart = new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);

    const timePart = new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
    }).format(date);

    return `${datePart}. ${timePart}`;
  } catch {
    return "";
  }
}

/**
 * Formats a date as a relative day phrase (e.g. "today", "yesterday", "2 days ago")
 * @param dateString - The date string to format (can be null or undefined)
 * @param currentTime - Current time in milliseconds (pass Date.now() at the call site for testability)
 * @param locale - The locale to use for formatting
 * @returns Relative day phrase or empty string if invalid
 *
 * @example
 * formatRelativeDays("2024-01-13", new Date("2024-01-15").getTime()) // "2 days ago"
 */
export function formatRelativeDays(
  dateString: string | null | undefined,
  currentTime: number,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const diffDays = Math.round(
      (date.getTime() - currentTime) / (1000 * 60 * 60 * 24),
    );

    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      diffDays,
      "day",
    );
  } catch {
    return "";
  }
}

/**
 * Formats a job posting date, returning "Recently" if the date is within the specified days threshold, otherwise returns the formatted date
 * @param dateString - The date string to format (can be null or undefined)
 * @param currentTime - Current time in milliseconds to use for comparison (for SSR consistency)
 * @param daysThreshold - Number of days to consider as "recently posted"
 * @param locale - The locale to use for formatting
 * @returns "Recently" if within the days threshold, otherwise formatted date string or "N/A" if invalid
 *
 * @example
 * formatJobPostingDate("2024-01-15", Date.now(), 5, "en") // "Recently" if within 5 days, else "1/15/24"
 * formatJobPostingDate("2024-01-15", Date.now(), 7, "en") // "Recently" if within 7 days
 * formatJobPostingDate(null, Date.now(), 5, "en") // "N/A"
 */
export function formatJobPostingDate(
  dateString: string | null | undefined,
  currentTime: number,
  daysThreshold: number,
  locale: string,
): string {
  if (!dateString) return formatDate(dateString, locale);

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return formatDate(dateString, locale);

    // Only do the "recently" check on the client side to avoid hydration mismatches
    if (typeof window === "undefined") {
      return formatDate(dateString, locale);
    }

    const now = new Date(currentTime);
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Only show "Recently" for past dates within the threshold (not future dates)
    if (diffDays >= 0 && diffDays <= daysThreshold) {
      return "Recently";
    }

    return formatDate(dateString, locale);
  } catch {
    return formatDate(dateString, locale);
  }
}
